// WarmHub's shape field format: the types and the constraints each takes. The
// Tool SDK reads a definition's fields with them (defs.ts), and
// shapeFieldProblems checks fields against them: the manifest shapes that
// `npm run build` and staging check, and the fields the hub agent proposes,
// so the reader and the checks cannot differ. The format is the meta-schema documented in
// @warmhub/sdk-ts 0.148.0 (dist/index.d.ts, above MAX_CONTENT_FIELD_BYTES) and
// the typed field's keys are those its reader accepts (dist/chunk-IXJEQEPN.js,
// the predicate `M`). See Design > Answer shapes, "Shape types and validation".

import { isJsonObject, type Json } from "./json.ts";

/** WarmHub's field types. Any other type name is not one WarmHub has. */
export const PRIMITIVES = ["number", "string", "boolean", "wref", "array"] as const;
/** One of WarmHub's field types, without a "?". */
export type Primitive = (typeof PRIMITIVES)[number];

/** A wref field's `binding`. */
export const BINDINGS = ["identity", "versioned", "either"];

const isNumber = (v: Json) => typeof v === "number";
const isString = (v: Json) => typeof v === "string";

/**
 * The keys a typed field of each type takes besides `type` and `description`,
 * each with the test of its value's kind. An object with `type` set to a
 * WarmHub type and any other key, or one of these with a value of the wrong
 * kind, is not a typed field: WarmHub reads it as a nested object.
 */
export const CONSTRAINTS: Record<Primitive, Record<string, (v: Json) => boolean>> = {
  string: { minLength: isNumber, maxLength: isNumber, pattern: isString, enum: Array.isArray },
  number: { minimum: isNumber, maximum: isNumber, integer: (v) => typeof v === "boolean" },
  wref: { shape: isString, binding: (v) => typeof v === "string" && BINDINGS.includes(v) },
  array: { items: (v) => typeof v !== "number" && typeof v !== "boolean", minItems: isNumber, maxItems: isNumber },
  boolean: {},
};

/**
 * The test of a constraint's value for a typed field of `type`, or undefined
 * when that type doesn't take the key. Only the table's own keys count, so a
 * key named like an Object method, such as `constructor`, is not a constraint.
 */
export function constraintTest(type: Primitive, key: string): ((v: Json) => boolean) | undefined {
  const takes = CONSTRAINTS[type];
  return Object.hasOwn(takes, key) ? takes[key] : undefined;
}

/** A field name or type without the "?" that marks it optional. */
export function withoutQuestion(text: string): string {
  return text.endsWith("?") ? text.slice(0, -1) : text;
}

/** The WarmHub type a type name names, with or without its "?"; undefined for a name WarmHub doesn't have. */
export function primitiveOf(type: string): Primitive | undefined {
  const base = withoutQuestion(type);
  return PRIMITIVES.find((p) => p === base);
}

// shapeFieldProblems: why fields aren't in WarmHub's format. WarmHub reads an
// object that isn't a valid typed field as a nested object, one field per key,
// so `{ type: "string", optional: true }` is read as two required fields,
// `type` and `optional`, rather than as an optional string.

const TYPES = `${PRIMITIVES.join(", ")}, each optionally ending in "?"`;

/** Why one type spec, at `path`, isn't in WarmHub's format; empty when it is. */
function specProblems(path: string, spec: Json): string[] {
  if (typeof spec === "string") return primitiveOf(spec) === undefined ? [`"${path}" has type "${spec}", which WarmHub doesn't have: its types are ${TYPES}`] : [];
  if (Array.isArray(spec)) {
    const [items, ...rest] = spec;
    return items === undefined || rest.length > 0 ? [`"${path}" is an array spec, which takes exactly one element, the items' type`] : specProblems(`${path}[]`, items);
  }
  if (!isJsonObject(spec)) return [`"${path}" is ${JSON.stringify(spec)}, which isn't a type: give a type name, a typed field such as { "type": "string" }, an array or a nested object`];
  const type = spec["type"];
  if (typeof type !== "string") return fieldsProblems(`${path}.`, spec);
  // A `type` that isn't one of WarmHub's types is wrong either way: as a typed
  // field, and as a nested object's field named "type".
  const primitive = primitiveOf(type);
  return primitive === undefined ? [`"${path}" has type "${type}", which WarmHub doesn't have: its types are ${TYPES}`] : typedFieldProblems(path, primitive, spec);
}

/** Why an object whose `type` names one of WarmHub's types isn't read as that typed field; empty when it is. */
function typedFieldProblems(path: string, primitive: Primitive, spec: { readonly [key: string]: Json }): string[] {
  const allowed = CONSTRAINTS[primitive];
  const settings = Object.entries(spec).filter(([k]) => k !== "type");
  const extra = settings.filter(([k]) => k !== "description" && constraintTest(primitive, k) === undefined).map(([k]) => k);
  const mistyped = settings.filter(([k, v]) => !(k === "description" ? isString(v) : constraintTest(primitive, k)?.(v) ?? true)).map(([k]) => k);
  const problems: string[] = [];
  if (extra.length > 0 || mistyped.length > 0) {
    const keys = ["type", "description", ...Object.keys(allowed)].map((k) => `"${k}"`).join(", ");
    const why = extra.length > 0 ? `it has ${extra.map((k) => `"${k}"`).join(", ")}, and a ${primitive} field takes only ${keys}` : `its ${mistyped.map((k) => `"${k}"`).join(", ")} has the wrong kind of value`;
    const optional = extra.some((k) => k === "optional" || k === "required") ? `. Mark an optional field with "?" after its name, as "${path.split(".").at(-1)}?", or after its type, as "${primitive}?"` : "";
    problems.push(`WarmHub reads "${path}" as a nested object with fields ${Object.keys(spec).map((k) => `"${k}"`).join(", ")}, not as a ${primitive} field, because ${why}${optional}`);
  }
  const items = spec["items"];
  if (primitive === "array" && items !== undefined && items !== null) problems.push(...specProblems(`${path}[]`, items));
  return problems;
}

/**
 * Why an answer shape's fields aren't in WarmHub's shape field format, one
 * message per problem; empty when they are. It accepts every form WarmHub
 * documents: a type name, a typed field with its type's constraints, `[T]` or
 * `{ type: "array", items: T }`, and a nested object, with "?" after a name or
 * a type marking it optional.
 */
export function shapeFieldProblems(fields: { readonly [name: string]: Json }): string[] {
  return fieldsProblems("", fields);
}

// The problems of an object's fields, each at `prefix` and its name. A field
// written twice, as `a` and `a?`, is one WarmHub's definition check refuses
// (@warmhub/sdk-ts 0.148.0, dist/chunk-IXJEQEPN.js, `le`, "duplicate field
// name"), and the Tool SDK's reader keeps only the first (defs.ts, readFields).
function fieldsProblems(prefix: string, fields: { readonly [name: string]: Json }): string[] {
  const seen = new Set<string>();
  return Object.entries(fields).flatMap(([name, spec]) => {
    const path = `${prefix}${withoutQuestion(name)}`;
    const twice = seen.has(path) ? [`"${path}" is written twice, with and without "?": WarmHub refuses a duplicate field name`] : [];
    seen.add(path);
    return [...twice, ...specProblems(path, spec)];
  });
}
